import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface PatientReferral {
  id: string;
  referring_patient_id: string;
  referred_patient_id: string;
  clinic_id: string;
  referral_date: string;
  status: 'pending' | 'completed';
  points_awarded: boolean;
  points_transaction_id?: string;
  created_at: string;
  referring_patient?: {
    id: string;
    name: string;
    phone: string;
    registration_number: string;
  };
  referred_patient?: {
    id: string;
    name: string;
    phone: string;
    registration_number: string;
  };
}

// Hook to get referrals made by a patient
export const usePatientReferrals = (referringPatientId?: string) => {
  return useQuery({
    queryKey: ["patient-referrals", referringPatientId],
    enabled: !!referringPatientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_referrals")
        .select(`
          *,
          referred_patient:patients!patient_referrals_referred_patient_id_fkey(id, name, phone, registration_number)
        `)
        .eq("referring_patient_id", referringPatientId!)
        .order("referral_date", { ascending: false });

      if (error) throw error;
      return data as PatientReferral[];
    },
  });
};

// Hook to check if a patient was referred
export const usePatientReferredBy = (patientId?: string) => {
  return useQuery({
    queryKey: ["patient-referred-by", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_referrals")
        .select(`
          *,
          referring_patient:patients!patient_referrals_referring_patient_id_fkey(id, name, phone, registration_number)
        `)
        .eq("referred_patient_id", patientId!)
        .maybeSingle();

      if (error) throw error;
      return data as PatientReferral | null;
    },
  });
};

// Hook to create a referral when patient is registered
export const useCreateReferral = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      referringPatientId,
      referredPatientId,
      clinicId,
    }: {
      referringPatientId: string;
      referredPatientId: string;
      clinicId: string;
    }) => {
      const { data, error } = await supabase
        .from("patient_referrals")
        .insert({
          referring_patient_id: referringPatientId,
          referred_patient_id: referredPatientId,
          clinic_id: clinicId,
          status: "pending",
          points_awarded: false,
        })
        .select()
        .single();

      if (error) throw error;

      // Also update the patient's referred_by_patient_id
      await supabase
        .from("patients")
        .update({ referred_by_patient_id: referringPatientId })
        .eq("id", referredPatientId);

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["patient-referrals", variables.referringPatientId] });
      queryClient.invalidateQueries({ queryKey: ["patient-referred-by", variables.referredPatientId] });
    },
    onError: (error: any) => {
      console.error("Failed to create referral:", error);
    },
  });
};

// Hook to complete a referral and award points
export const useCompleteReferral = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      referralId,
      referringPatientId,
      clinicId,
      bonusPoints,
    }: {
      referralId: string;
      referringPatientId: string;
      clinicId: string;
      bonusPoints: number;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) throw new Error("You must be logged in");

      // Get or create points record for referring patient
      const { data: pointsId, error: rpcError } = await supabase
        .rpc("get_or_create_smile_points", {
          p_patient_id: referringPatientId,
          p_clinic_id: clinicId,
        });

      if (rpcError) throw rpcError;

      // Create transaction for referral bonus
      const { data: transaction, error: transactionError } = await supabase
        .from("smile_points_transactions")
        .insert({
          patient_points_id: pointsId,
          transaction_type: "earned_referral",
          points: bonusPoints,
          reference_type: "referral",
          reference_id: referralId,
          description: `Referral bonus: ${bonusPoints} points`,
          created_by: user.id,
        })
        .select()
        .single();

      if (transactionError) throw transactionError;

      // Update referral status
      const { data, error } = await supabase
        .from("patient_referrals")
        .update({
          status: "completed",
          points_awarded: true,
          points_transaction_id: transaction.id,
        })
        .eq("id", referralId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["patient-referrals", variables.referringPatientId] });
      queryClient.invalidateQueries({ queryKey: ["patient-smile-points", variables.referringPatientId, variables.clinicId] });
      toast.success(`Referral bonus of ${variables.bonusPoints} points awarded!`);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to complete referral");
    },
  });
};

// Hook to get all referrals for a clinic (admin view)
export const useAllClinicReferrals = (clinicId?: string) => {
  return useQuery({
    queryKey: ["all-clinic-referrals", clinicId],
    enabled: !!clinicId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_referrals")
        .select(`
          *,
          referring_patient:patients!patient_referrals_referring_patient_id_fkey(id, name, phone, registration_number),
          referred_patient:patients!patient_referrals_referred_patient_id_fkey(id, name, phone, registration_number)
        `)
        .eq("clinic_id", clinicId!)
        .order("referral_date", { ascending: false });

      if (error) throw error;
      return data as PatientReferral[];
    },
  });
};
